import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Images } from "./components/Images";
import { processImages, initializeModel, getModelInfo } from "../lib/process";

interface AppError {
  message: string;
}

export interface ImageFile {
  id: number;
  file: File;
  processedFile?: File;
}

// Sample images from Unsplash
const sampleImages = [
  "https://images.unsplash.com/photo-1618886614638-80e3c103d31a?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1437622368342-7a3d73a34c8f?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?q=80&w=2688&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1588516903720-8ceb67f9ef84?q=80&w=3144&auto=format&fit=crop&ixlib=rb-4.0.3"
];

// Check if the user is on mobile Safari
const isMobileSafari = () => {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  const iOSSafari = iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/OPiOS/i) && !ua.match(/FxiOS/i);
  return iOSSafari && 'ontouchend' in document;
};

// 功能介绍卡片数据
const featureCards = [
  {
    icon: "magic",
    title: "智能抠图",
    description: "先进的AI技术，一键去除任何图片背景，无需手动操作"
  },
  {
    icon: "shield-alt",
    title: "隐私保障",
    description: "所有处理在本地完成，图片不会上传到任何服务器"
  },
  {
    icon: "paint-brush",
    title: "自定义背景",
    description: "轻松更换背景颜色或图片，打造完美效果"
  },
  {
    icon: "download",
    title: "高质量导出",
    description: "支持透明背景PNG格式导出，满足各种专业需求"
  }
];

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [isWebGPU, setIsWebGPU] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [currentModel, setCurrentModel] = useState<'briaai/RMBG-1.4' | 'Xenova/modnet'>('briaai/RMBG-1.4');
  const [isModelSwitching, setIsModelSwitching] = useState(false);
  const [images, setImages] = useState<ImageFile[]>([]);
  const [activeNavItem, setActiveNavItem] = useState('home');

  useEffect(() => {
    if (isMobileSafari()) {
      window.location.href = 'https://cuttypic.toolooz.com';
      return;
    }

    // Only check iOS on load since that won't change
    const { isIOS: isIOSDevice } = getModelInfo();
    setIsIOS(isIOSDevice);
    setIsLoading(false);
  }, []);

  const handleModelChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = event.target.value as typeof currentModel;
    setIsModelSwitching(true);
    setError(null);
    try {
      const initialized = await initializeModel(newModel);
      if (!initialized) {
        throw new Error("Failed to initialize new model");
      }
      setCurrentModel(newModel);
    } catch (err) {
      if (err instanceof Error && err.message.includes("Falling back")) {
        setCurrentModel('briaai/RMBG-1.4');
      } else {
        setError({
          message: err instanceof Error ? err.message : "Failed to switch models"
        });
      }
    } finally {
      setIsModelSwitching(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newImages = acceptedFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      processedFile: undefined
    }));
    setImages(prev => [...prev, ...newImages]);
    
    // Initialize model if this is the first image
    if (images.length === 0) {
      setIsLoading(true);
      setError(null);
      try {
        const initialized = await initializeModel();
        if (!initialized) {
          throw new Error("Failed to initialize background removal model");
        }
        // Update WebGPU support status after model initialization
        const { isWebGPUSupported } = getModelInfo();
        setIsWebGPU(isWebGPUSupported);
      } catch (err) {
        setError({
          message: err instanceof Error ? err.message : "An unknown error occurred"
        });
        setImages([]); // Clear the newly added images if model fails to load
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    
    for (const image of newImages) {
      try {
        const result = await processImages([image.file]);
        if (result && result.length > 0) {
          setImages(prev => prev.map(img =>
            img.id === image.id
              ? { ...img, processedFile: result[0] }
              : img
          ));
        }
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  }, [images.length]);


  const handlePaste = async (event: React.ClipboardEvent) => {
    const clipboardItems = event.clipboardData.items;
    const imageFiles: File[] = [];
    for (const item of clipboardItems) {
      if (item.type.startsWith("image")) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length > 0) {
      onDrop(imageFiles);
    }
  };  

  const handleSampleImageClick = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], 'sample-image.jpg', { type: 'image/jpeg' });
      onDrop([file]);
    } catch (error) {
      console.error('Error loading sample image:', error);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".mp4"],
    },
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-blue-50" onPaste={handlePaste}>
      {/* 导航栏 */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <a href="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
                  <img src="/favicon.svg" alt="抠小宝" className="w-8 h-8" />
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                    抠小宝
                  </h1>
                </a>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-center space-x-4">
                  <button 
                    onClick={() => setActiveNavItem('home')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeNavItem === 'home' ? 'text-pink-500' : 'text-gray-600 hover:text-pink-500'}`}
                  >
                    <i className="fas fa-home mr-1"></i> 首页
                  </button>
                  <button 
                    onClick={() => window.location.href = '/guide.html'}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeNavItem === 'guide' ? 'text-pink-500' : 'text-gray-600 hover:text-pink-500'}`}
                  >
                    <i className="fas fa-book mr-1"></i> 使用指南
                  </button>
                  <button 
                    onClick={() => window.location.href = '/about.html'}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeNavItem === 'about' ? 'text-pink-500' : 'text-gray-600 hover:text-pink-500'}`}
                  >
                    <i className="fas fa-info-circle mr-1"></i> 关于我们
                  </button>
                </div>
              </div>
            </div>
            {!isIOS && (
              <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full">
                <i className="fas fa-microchip text-purple-500"></i>
                <span className="text-gray-600 text-sm">模型:</span>
                <select
                  value={currentModel}
                  onChange={handleModelChange}
                  className="bg-white border border-gray-200 rounded-full px-3 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 shadow-sm"
                  disabled={!isWebGPU}
                >
                  <option value="briaai/RMBG-1.4">RMBG-1.4 (通用版)</option>
                  {isWebGPU && (
                    <option value="Xenova/modnet">MODNet (WebGPU版)</option>
                  )}
                </select>
              </div>
            )}
          </div>
          {isIOS && (
            <p className="text-sm text-gray-500 mt-2">
              使用针对iOS优化的抠图模型
            </p>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {images.length === 0 ? (
          <div className="space-y-12">
            {/* 英雄区域 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="flex flex-col justify-center">
                <div className="inline-block bg-pink-100 text-pink-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
                  <i className="fas fa-star text-yellow-500 mr-1"></i> 智能抠图工具
                </div>
                <h2 className="text-4xl font-extrabold text-gray-800 mb-4 leading-tight">
                  智能一键抠图，<br />
                  <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">让创作更简单</span>
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  100% 自动处理，完全免费，无需注册
                </p>
                <p className="text-gray-500 mb-6">
                  上传您的图片，让我们的AI立即去除背景。适用于专业照片、产品图片等多种场景。
                </p>
                <div className="flex items-center text-sm text-gray-400">
                  <i className="fas fa-shield-alt text-green-500 mr-1"></i>
                  <span>所有处理在本地完成，保障您的隐私安全</span>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-yellow-200 rounded-full opacity-50"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-pink-200 rounded-full opacity-50"></div>
                <img 
                  src="hero.png"
                  alt="抠图示例"
                  className="relative z-10 rounded-2xl shadow-xl w-full object-cover h-[400px]"
                />
              </div>
            </div>

            {/* 上传区域 */}
            <div
              {...getRootProps()}
              className={`p-10 border-3 border-dashed rounded-2xl text-center cursor-pointer transition-all duration-300 ease-in-out bg-white shadow-sm hover:shadow-md relative overflow-hidden
                ${isDragAccept ? "border-green-500 bg-green-50" : ""}
                ${isDragReject ? "border-red-500 bg-red-50" : ""}
                ${isDragActive ? "border-blue-500 bg-blue-50" : "border-pink-300 hover:border-pink-500 hover:bg-pink-50"}
                ${isLoading || isModelSwitching ? "cursor-not-allowed" : ""}
              `}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-100 rounded-full opacity-30"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-100 rounded-full opacity-30"></div>
              
              <input {...getInputProps()} className="hidden" disabled={isLoading || isModelSwitching} />
              <div className="flex flex-col items-center gap-4 relative z-10">
                {isLoading || isModelSwitching ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-pink-600 mb-2"></div>
                    <p className="text-xl text-gray-600 font-medium">
                      {isModelSwitching ? '正在切换模型...' : '正在加载抠图模型...'}
                    </p>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                      首次加载可能需要一些时间，请耐心等待
                    </p>
                  </>
                ) : error ? (
                  <>
                    <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-2">
                      <i className="fas fa-exclamation-triangle text-3xl text-red-500"></i>
                    </div>
                    <p className="text-xl text-red-600 font-medium mb-2">{error.message}</p>
                    {currentModel === 'Xenova/modnet' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModelChange({ target: { value: 'briaai/RMBG-1.4' }} as any);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:opacity-90 transition-opacity shadow-md"
                      >
                        <i className="fas fa-sync-alt mr-2"></i>
                        切换到通用版本
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-24 h-24 rounded-full bg-pink-100 flex items-center justify-center mb-4">
                      <i className="fas fa-cloud-upload-alt text-4xl text-pink-500"></i>
                    </div>
                    <p className="text-2xl font-medium text-gray-700 mb-2">
                      {isDragActive
                        ? "松开鼠标上传图片..."
                        : "拖拽图片到这里上传"}
                    </p>
                    <p className="text-gray-500 mb-4">或</p>
                    <button className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:opacity-90 transition-opacity shadow-md">
                      <i className="fas fa-image mr-2"></i>
                      选择文件
                    </button>
                    <p className="text-sm text-gray-400 mt-4">
                      支持 JPG、PNG、JPEG 格式
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* 示例图片区域 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl text-gray-700 font-semibold mb-6 flex items-center">
                <i className="fas fa-images text-purple-500 mr-2"></i>
                没有图片？试试这些:
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {sampleImages.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => handleSampleImageClick(url)}
                    className="relative aspect-square overflow-hidden rounded-xl hover:opacity-90 transition-all focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm hover:shadow-md transform hover:-translate-y-1"
                  >
                    <img
                      src={url}
                      alt={`示例 ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
                      <span className="text-white text-sm font-medium">使用此图片</span>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-6 flex items-center">
                <i className="fas fa-lock text-green-500 mr-2"></i>
                所有图片均在您的设备上本地处理，不会上传到任何服务器。
              </p>
            </div>

            {/* 功能介绍卡片 */}
            <div>
              <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
                <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">强大功能</span>，简单操作
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {featureCards.map((card, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                    <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center mb-4">
                      <i className={`fas fa-${card.icon} text-2xl text-pink-500`}></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{card.title}</h4>
                    <p className="text-gray-600">{card.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 小贴士区域 */}
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex-shrink-0 flex items-center justify-center">
                  <i className="fas fa-lightbulb text-2xl text-yellow-500"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">小贴士：为什么抠图可能需要一些时间？</h3>
                  <div className="space-y-3 text-gray-600">
                    <p><i className="fas fa-brain text-purple-500 mr-2"></i> <strong>复杂的AI模型：</strong>背景去除模型（如RMBG-1.4）是复杂的深度学习模型，需要大量计算资源。</p>
                    <p><i className="fas fa-globe text-blue-500 mr-2"></i> <strong>浏览器环境限制：</strong>在浏览器中运行AI模型受到以下限制：WebAssembly执行速度比原生应用慢，浏览器的内存和计算资源受限。</p>
                    <p><i className="fas fa-image text-green-500 mr-2"></i> <strong>图像大小：</strong>处理高分辨率图像需要更多计算资源和时间。</p>
                    <p><i className="fas fa-laptop text-red-500 mr-2"></i> <strong>设备性能：</strong>用户设备的CPU/GPU性能直接影响处理速度。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="mb-8">
              <div
                {...getRootProps()}
                className={`p-6 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all duration-300 ease-in-out bg-white shadow-sm hover:shadow-md relative overflow-hidden
                  ${isDragAccept ? "border-green-500 bg-green-50" : ""}
                  ${isDragReject ? "border-red-500 bg-red-50" : ""}
                  ${isDragActive ? "border-blue-500 bg-blue-50" : "border-pink-300 hover:border-pink-500 hover:bg-pink-50"}
                  ${isLoading || isModelSwitching ? "cursor-not-allowed" : ""}
                `}
              >
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-purple-100 rounded-full opacity-30"></div>
                <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-pink-100 rounded-full opacity-30"></div>
                
                <input {...getInputProps()} className="hidden" disabled={isLoading || isModelSwitching} />
                <div className="flex items-center justify-center gap-4 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                    <i className="fas fa-cloud-upload-alt text-xl text-pink-500"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-medium text-gray-700">
                      {isDragActive
                        ? "松开鼠标上传更多图片..."
                        : "拖拽图片到这里上传更多图片"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <button className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm rounded-full hover:opacity-90 transition-opacity shadow-sm">
                        <i className="fas fa-image mr-1"></i>
                        选择文件
                      </button>
                      <span className="text-sm text-gray-500">支持 JPG、PNG、JPEG 格式</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Images images={images} onDelete={(id) => setImages(prev => prev.filter(img => img.id !== id))} />
          </div>
        )}
      </main>

      {/* 页脚 */}
      <footer className="bg-white mt-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <a href="/" className="flex items-center gap-2 mb-4 hover:opacity-90 transition-opacity">
                <img 
                  src="/favicon-small.svg" 
                  alt="抠小宝" 
                  className="w-4 h-4 flex-shrink-0" 
                  style={{ width: '16px', height: '16px' }}
                />
                <h3 className="text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                  抠小宝
                </h3>
              </a>
              <p className="text-gray-500 text-sm">
                智能一键抠图工具，让创作更简单
              </p>
              <div className="flex space-x-4 mt-4">
                <a href="#" className="text-gray-400 hover:text-pink-500 transition-colors" title="微信号: liqidashi2013">
                  <i className="fab fa-weixin text-xl"></i>
                </a>
                <a href="https://weibo.com/u/3937088387" target="_blank" className="text-gray-400 hover:text-pink-500 transition-colors">
                  <i className="fab fa-weibo text-xl"></i>
                </a>
                <a href="https://github.com/naxiaoduo" target="_blank" className="text-gray-400 hover:text-pink-500 transition-colors">
                  <i className="fab fa-github text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-800 mb-4">快速链接</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 首页</a></li>
                <li><a href="/guide.html" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 使用指南</a></li>
                <li><a href="/about.html" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 关于我们</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-800 mb-4">法律信息</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy.html" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 隐私政策</a></li>
                <li><a href="/terms.html" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 使用条款</a></li>
                <li><a href="/copyright.html" className="text-gray-500 hover:text-pink-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-pink-300"></i> 版权信息</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-800 mb-4">联系我们</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-gray-500 flex items-start"><i className="fas fa-envelope mt-1 mr-2 text-pink-400"></i> support@toolooz.com</li>
                <li className="text-gray-500 flex items-start"><i className="fas fa-map-marker-alt mt-1 mr-2 text-pink-400"></i> 中国，数字游民</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} 抠小宝 - 由Toolooz倾情打造
            </p>
            <p className="text-sm text-gray-400 mt-2 md:mt-0">
              <i className="fas fa-code text-pink-300 mr-1"></i> 使用 React + Vite + Tailwind 构建
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
